import { 
    Button, 
    FormControl, 
    FormLabel, 
    Input, 
    Modal, 
    ModalClose, 
    ModalDialog, 
    ModalOverflow, 
    Stack, 
    Typography, 
    Alert,
    CircularProgress,
    Box,
    Divider
} from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";

export default function ModalDangerKeyword({ 
    open, 
    onClose, 
    mode = 'create', // 'create' | 'edit' | 'delete'
    keyword: initialKeyword = null,
    onSave,
    onDelete,
    loading = false 
}) {
    const [formData, setFormData] = useState({
        keyword: ''
    });
    const [error, setError] = useState(null);

    // Сброс формы при открытии/закрытии
    useEffect(() => {
        if (open) {
            if (mode === 'edit' && initialKeyword) {
                setFormData({
                    keyword: initialKeyword.keyword || ''
                });
            } else {
                setFormData({
                    keyword: ''
                });
            }
            setError(null);
        }
    }, [open, mode, initialKeyword]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = () => {
        if (!formData.keyword.trim()) {
            setError('Введите ключевое слово');
            return;
        }

        if (mode === 'create' || mode === 'edit') {
            onSave(formData.keyword.trim());
        }
    };

    const handleDelete = () => {
        if (mode === 'delete' && initialKeyword) {
            onDelete(initialKeyword.id);
        }
    };

    const getTitle = () => {
        switch (mode) {
            case 'create':
                return 'Добавить ключевое слово';
            case 'edit':
                return 'Редактировать ключевое слово';
            case 'delete':
                return 'Удалить ключевое слово';
            default:
                return '';
        }
    };

    const getActionButtonText = () => {
        switch (mode) {
            case 'create':
                return 'Создать';
            case 'edit':
                return 'Сохранить';
            case 'delete':
                return 'Удалить';
            default:
                return '';
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <Modal
                    sx={{ zIndex: 10010 }}
                    open={open}
                    onClose={onClose}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3, ease: "easeInOut" },
                        }
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto' }}>
                        <ModalDialog
                            size="md"
                            component={motion.div}
                            initial={{ opacity: 0, y: -10, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.98 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            sx={(theme) => ({
                                [theme.breakpoints.up("xl")]: { width: '500px', mx: 0 },
                                [theme.breakpoints.down("xl")]: { width: '500px', mx: 0 },
                                [theme.breakpoints.down("lg")]: { width: '450px', mx: 0 },
                                [theme.breakpoints.down("md")]: { width: '400px', mx: 0 },
                                [theme.breakpoints.down("sm")]: { width: '95%', p: 3 },
                                width: '100%'
                            })}
                        >
                            <ModalClose onClick={onClose} />
                            
                            <Typography sx={{ textAlign: 'center', mb: 2, mt: 2 }} level="h3">
                                {getTitle()}
                            </Typography>

                            {error && (
                                <Alert color="danger" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            {mode === 'delete' ? (
                                // Контент для удаления
                                <Box>
                                    <Typography sx={{ mb: 2 }}>
                                        Вы уверены, что хотите удалить ключевое слово 
                                        <Typography fontWeight="bold" component="span"> "{initialKeyword?.keyword}"</Typography>?
                                    </Typography>
                                    <Typography level="body2" color="neutral">
                                        Это действие нельзя отменить.
                                    </Typography>
                                    
                                    <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                                        <Button
                                            variant="outlined"
                                            color="neutral"
                                            fullWidth
                                            onClick={onClose}
                                            disabled={loading}
                                        >
                                            Отмена
                                        </Button>
                                        <Button
                                            variant="solid"
                                            color="danger"
                                            fullWidth
                                            onClick={handleDelete}
                                            loading={loading}
                                        >
                                            {getActionButtonText()}
                                        </Button>
                                    </Stack>
                                </Box>
                            ) : (
                                // Контент для создания/редактирования
                                <Stack spacing={3}>
                                    <FormControl required>
                                        <FormLabel>Ключевое слово</FormLabel>
                                        <Input 
                                            name="keyword"
                                            value={formData.keyword}
                                            onChange={handleChange}
                                            placeholder="Введите ключевое слово..."
                                            autoFocus
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleSubmit();
                                                }
                                            }}
                                        />
                                    </FormControl>

                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            variant="outlined"
                                            color="neutral"
                                            fullWidth
                                            onClick={onClose}
                                            disabled={loading}
                                        >
                                            Отмена
                                        </Button>
                                        <Button
                                            variant="solid"
                                            color="primary"
                                            fullWidth
                                            onClick={handleSubmit}
                                            loading={loading}
                                            disabled={!formData.keyword.trim()}
                                        >
                                            {getActionButtonText()}
                                        </Button>
                                    </Stack>
                                </Stack>
                            )}
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}